/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { getColor, getShape } from '@airasia-phoenix/react-native/utils/utils';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useRouter } from 'expo-router';
import { useLogSheetLoginSuccess } from '../../hooks/useLogSheetLoginSuccess';
import { Modal, TouchableWithoutFeedback, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import LoginView from '../../widgets/LoginView/LoginView';
import { defaultProps } from '../../widgets/LoginView/types';
import ForgotPasswordView from '../../widgets/ForgotPasswordView/ForgotPasswordView';
import { defaultProps as forgotPasswordDefaultProps } from '../../widgets/ForgotPasswordView/types';
import SignUpView from '../../widgets/SignUpView/SignUpView';
import { defaultProps as signUpDefaultProps } from '../../widgets/SignUpView/types';
import SignUpFormView from '../../widgets/SignUpFormView/SignUpFormView';
import { defaultProps as signUpFormDefaultProps } from '../../widgets/SignUpFormView/types';
import VerifyPhoneView from '../VerifyPhoneView/VerifyPhoneView';
import { Purpose } from '../../managers/sso';
import VerifyEmailView from '../VerifyEmailView';
import { FORGOT_PASSWORD_FLOW, ROUTES } from '../../constants/routes';
import ScreenTransition, {
  TransitionType,
  ScreenType,
} from '../../utils/ScreenTransition';
import { BottomSheetModalProvider } from '@gorhom/bottom-sheet';
import {
  MobileLoginType,
  PasswordlessLoginResult,
  SheetLoginSuccessData,
} from '../../widgets/LoginView/types';
import type { TwoFaLoginData } from '../../hooks/useEmailLoginSubmit';

export type LoginSheetProps = {
  visible: boolean;
  onClose: () => void;
  onForgotPasswordClick?: () => void;
  onLoginSuccess?: (data: SheetLoginSuccessData) => void;
};

// Define screen configuration array outside component for better performance
const screenConfigs = [
  { type: ScreenType.LOGIN, transition: TransitionType.FADE_SLIDE },
  { type: ScreenType.FORGOT_PASSWORD, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.SIGNUP, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_ACCOUNT, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_OTP, transition: TransitionType.SLIDE_RIGHT },
  { type: ScreenType.VERIFY_EMAIL_OTP, transition: TransitionType.SLIDE_RIGHT },
];

// LoginView and SignUpView have no side padding of their own (the full-screen
// pages pad them); the other screens pad themselves.
const UNPADDED_SCREENS = [ScreenType.LOGIN, ScreenType.SIGNUP];

const LoginSheet: React.FC<LoginSheetProps> = ({
  visible,
  onClose,
  onForgotPasswordClick,
  onLoginSuccess,
}) => {
  const router = useRouter();
  const logLoginSuccess = useLogSheetLoginSuccess('LoginSheet');
  const [currentScreen, setCurrentScreen] = useState<ScreenType>(
    ScreenType.LOGIN,
  );
  const [signUpData, setSignUpData] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [mobileLoginData, setMobileLoginData] =
    useState<MobileLoginType | null>(null);

  // Drives the VERIFY_EMAIL_OTP screen: forgot password, passwordless login
  // and email 2FA all verify an emailed code.
  const [emailOtp, setEmailOtp] = useState<{
    email?: string;
    purpose: Purpose;
    cancelDeletion?: boolean;
  } | null>(null);

  const handleForgotPasswordEmailSent = useCallback((email: string) => {
    setEmailOtp({ email, purpose: Purpose.FORGOT_PASSWORD });
    setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
  }, []);

  const handlePasswordlessLoginSuccess = useCallback(
    (data: PasswordlessLoginResult) => {
      setEmailOtp({
        email: data.email,
        purpose: Purpose.EMAIL_OTP_VERIFICATION_LOGIN,
        cancelDeletion: data.cancelDeletion,
      });
      setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
    },
    [],
  );

  const handleTwoFactorRequired = useCallback(({ email }: TwoFaLoginData) => {
    setEmailOtp({ email, purpose: Purpose.LOGIN_2FA });
    setCurrentScreen(ScreenType.VERIFY_EMAIL_OTP);
  }, []);

  const handleForgotPasswordClick = () => {
    setCurrentScreen(ScreenType.FORGOT_PASSWORD);
    onForgotPasswordClick?.();
  };

  const handleBackToLogin = () => {
    setCurrentScreen(ScreenType.LOGIN);
    setEmailOtp(null);
    setSignUpData(null); // Reset signup data when going back to login
    setMobileLoginData(null); // Reset mobile login data when going back
  };

  const handleClose = () => {
    setCurrentScreen(ScreenType.LOGIN);
    setEmailOtp(null);
    setSignUpData(null); // Reset signup data when closing
    setMobileLoginData(null); // Reset mobile login data when closing
    onClose();
  };

  const handleSignUpClick = () => {
    setCurrentScreen(ScreenType.SIGNUP);
  };

  const handleSignUpSuccess = (data: { email: string; password: string }) => {
    setSignUpData(data);
    setCurrentScreen(ScreenType.VERIFY_ACCOUNT);
  };

  const handleMobileLoginPress = useCallback((data: MobileLoginType) => {
    setMobileLoginData(data);
  }, []);

  const handleMobileLoginSuccess = useCallback(() => {
    setCurrentScreen(ScreenType.VERIFY_OTP);
  }, []);

  const handleForgotPasswordOtpVerified = () => {
    handleClose();
    router.push({
      pathname: ROUTES.ACCOUNT_CREATE_NEW_PASSWORD,
      params: { flow: FORGOT_PASSWORD_FLOW },
    });
  };

  // Reset to LOGIN screen when modal opens (only when transitioning from closed to open)
  const prevVisibleRef = useRef(visible);
  useEffect(() => {
    if (visible && !prevVisibleRef.current) {
      setCurrentScreen(ScreenType.LOGIN);
      setMobileLoginData(null);
      setEmailOtp(null);
    }
    prevVisibleRef.current = visible;
  }, [visible]);

  const dialingCodeForOtp = mobileLoginData?.dialingCode?.startsWith('+')
    ? mobileLoginData.dialingCode.replace('+', '')
    : (mobileLoginData?.dialingCode ?? '');

  const renderCurrentScreen = () => {
    switch (currentScreen) {
      case ScreenType.LOGIN:
        return (
          <LoginView
            {...defaultProps}
            onCloseIconClick={handleClose}
            onForgotPasswordClick={handleForgotPasswordClick}
            onSignUpClick={handleSignUpClick}
            onLoginSuccess={(data) => {
              void logLoginSuccess((data as { method?: string }).method);
              onLoginSuccess?.(data);
              onClose();
            }}
            onMobileLoginPress={handleMobileLoginPress}
            onMobileLoginSuccess={handleMobileLoginSuccess}
            onPasswordLessEmailLoginSuccess={handlePasswordlessLoginSuccess}
            onTwoFactorRequired={handleTwoFactorRequired}
          />
        );
      case ScreenType.FORGOT_PASSWORD:
        return (
          <ForgotPasswordView
            {...forgotPasswordDefaultProps}
            onSendEmailSuccess={handleForgotPasswordEmailSent}
            onBackPress={handleBackToLogin}
          />
        );
      case ScreenType.SIGNUP:
        return (
          <SignUpView
            {...signUpDefaultProps}
            onCloseIconClick={handleBackToLogin}
            onProceedToPersonalDetails={handleSignUpSuccess}
          />
        );
      case ScreenType.VERIFY_ACCOUNT:
        return (
          <SignUpFormView
            {...signUpFormDefaultProps}
            onCloseIconClick={handleBackToLogin}
            email={signUpData?.email}
            password={signUpData?.password}
            onSignUpSuccess={handleBackToLogin}
          />
        );
      case ScreenType.VERIFY_OTP:
        return (
          <VerifyPhoneView
            purpose={Purpose.LOGIN_2FA}
            phoneNumber={mobileLoginData?.mobileNumber ?? ''}
            dialingCode={dialingCodeForOtp}
            onVerified={(result) => {
              void logLoginSuccess('mobile');
              onLoginSuccess?.(result?.session);
              handleClose();
            }}
            onUpdateMobileNumber={handleBackToLogin}
            showTitle={true}
          />
        );
      case ScreenType.VERIFY_EMAIL_OTP:
        return (
          <VerifyEmailView
            email={emailOtp?.email}
            purpose={emailOtp?.purpose ?? Purpose.FORGOT_PASSWORD}
            cancelDeletion={emailOtp?.cancelDeletion}
            onVerified={(result) => {
              if (emailOtp?.purpose === Purpose.FORGOT_PASSWORD) {
                handleForgotPasswordOtpVerified();
                return;
              }
              // Verifying the code has persisted the session, as for mobile OTP.
              void logLoginSuccess('email');
              onLoginSuccess?.(result?.session);
              handleClose();
            }}
          />
        );
      default:
        return null;
    }
  };

  // Padding belongs to the screen, not the shared container, so it moves
  // with the screen during the slide instead of snapping on the parent.
  const renderScreen = () =>
    UNPADDED_SCREENS.includes(currentScreen) ? (
      <View style={styles.screenPadding}>{renderCurrentScreen()}</View>
    ) : (
      renderCurrentScreen()
    );

  return (
    <Modal
      transparent
      visible={visible}
      animationType="fade"
      onRequestClose={handleClose}
    >
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.overlay}>
          <TouchableWithoutFeedback onPress={() => {}}>
            <View style={styles.container}>
              <BottomSheetModalProvider>
                <View style={styles.screenContainer} key={currentScreen}>
                  <ScreenTransition
                    key={currentScreen}
                    isVisible={true}
                    transitionType={
                      screenConfigs.find((c) => c.type === currentScreen)
                        ?.transition ?? TransitionType.SLIDE_RIGHT
                    }
                  >
                    {renderScreen()}
                  </ScreenTransition>
                </View>
              </BottomSheetModalProvider>
            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

export default LoginSheet;

const styles = StyleSheet.create((pd) => ({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: getColor(pd.sys.colour.overlay),
  },
  container: {
    ...getShape(pd.sys.shape.corner.large),
    width: 500,
    maxWidth: '90%',
    backgroundColor: getColor(pd.sys.colour.bg_primary),
    borderWidth: 1,
    elevation: 5,
    borderColor: getColor(pd.sys.colour.outline_low),
    paddingVertical: pd.ref.space.spacing40.px,
  },
  screenContainer: {
    position: 'relative',
    width: '100%',
    overflow: 'hidden',
  },
  screenPadding: {
    paddingHorizontal: pd.ref.space.spacing40.px,
  },
}));
